import {
  AfterViewInit,
  ComponentRef,
  Directive,
  ElementRef,
  HostListener,
  Injector,
  OnDestroy,
  OnInit,
  ViewContainerRef,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { EditDocumentSectionControlComponent } from '../../../shared/components/document-editing/edit-document-section-control/edit-document-section-control.component';
import { ShiveRenderService } from '../../../core/services/rendering/shive-render.service';
import {
  editModeCSSClass,
  editSectionHoveredCSSClass,
  editSectionWrapperCSSClass,
  editableAreaCSSClass,
  invisibleHTMLSectionCSSClass,
  invoicePreviewContainerWrapperCSSId,
  removedHTMLSectionCSSClass,
} from '../../../core/constants-repository/edit-document-sections.constants';
import { TouchAndMouseHandlerService } from '../../../core/services/touch-and-mouse-handler.service';
import { SubSink } from 'subsink';
import { StylingConfig } from '../../../shared/data-structures/styling-config';
import { InvoiceHandlingMode, SectionId, SectionType } from '../theme.types';
import { EditDocumentAreaService } from '../../../core/services/features/document-editing/edit-document-area.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, skip } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { ContentArea } from '../../../core/models/invoice/invoice-document-detail.model';
import { KeyValueRecord } from '../../../shared/functions/record-functions';

@Directive({
  selector: '[appEditDocumentSections]',
  standalone: true,
})
export class EditDocumentSectionDirective implements OnInit, AfterViewInit, OnDestroy {
  mode = input.required<InvoiceHandlingMode>();
  sectionId = input.required<SectionId>();
  contentArea = input.required<ContentArea>();
  editWrapperConfig = input<StylingConfig>(); // Add CSS styles and classes to the wrapper.
  meta = input<KeyValueRecord[]>(); // Add CSS styles and classes to the wrapper.

  private static currentlyActiveSection: HTMLElement = null;
  // Signals
  private readonly isHidden = signal<boolean>(false);
  private readonly isRemoved = signal<boolean>(false);

  private readonly subs = new SubSink();
  private controlsRef: ComponentRef<EditDocumentSectionControlComponent> | null = null;
  private wrapperElement: HTMLElement;

  // DI
  private readonly vc = inject(ViewContainerRef);
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly shiveRenderService = inject(ShiveRenderService);
  private readonly mouseHandlerService = inject(TouchAndMouseHandlerService);
  private readonly editDocumentAreaService = inject(EditDocumentAreaService, { optional: true });

  // Create an observable in a field initializer (injection context),
  // and provide the Injector explicitly.
  private readonly injector = inject(Injector);
  private readonly selected$ = this.editDocumentAreaService
    ? toObservable(this.editDocumentAreaService.selected, { injector: this.injector })
    : EMPTY;

  private readonly hovered$ = this.editDocumentAreaService
    ? toObservable(this.editDocumentAreaService.hovered, { injector: this.injector })
    : EMPTY;

  constructor() {
    effect(() => {
      this.editDocumentAreaService.fillDynamicFields(this.meta(), this.el);
    });
  }

  ngOnInit(): void {
    const meta = this.contentArea().meta;

    // Set initial 'is-removed'.
    const isRemoved = this.editDocumentAreaService.isRemoved(meta);
    this.isRemoved.set(isRemoved);

    if (!isRemoved) {
      // Set initial 'is-hidden'.
      const isHidden = this.editDocumentAreaService.isHidden(meta);
      this.isHidden.set(isHidden);
    }

    if (this.mode() === 'print') {
      return;
    }

    this.initSelection();
    this.initHover();
  }

  ngAfterViewInit(): void {
    // React to isHidden() changes.
    this.applyIsHiddenOrRemovedStyling();

    if (this.mode() === 'print') {
      // If we are in print mode, our wrapper element is the current section.
      this.wrapperElement = this.el.nativeElement;
      return;
    }

    this.shiveRenderService.addClass(this.el.nativeElement, 'k-pos-relative');
    this.shiveRenderService.addClass(this.el.nativeElement, editableAreaCSSClass);

    this.wrapContent();

    // Remove control component when the cursor is outside the edit-area section.
    this.subs.sink = this.mouseHandlerService.globalMouseMoved$.subscribe((event) => {
      const from = event.target as Node;
      if (from && this.wrapperElement.contains(from)) {
        return; // we’re just coming from our own overlay.
      }

      if (!this.controlsRef) {
        return;
      }

      this.destroyEditSectionControl();
      this.shiveRenderService.removeClass(this.el.nativeElement, `${editSectionHoveredCSSClass}`);
      this.editDocumentAreaService.hover(null);
    });

    // Hide text or image editor if user clicks outside of editable area.
    this.subs.sink = this.mouseHandlerService.globalMouseReleased$.subscribe((event) => {
      const previewContainerWrapper = document.querySelector(
        `#${invoicePreviewContainerWrapperCSSId}`,
      );
      const clickedElement = event.target as HTMLElement;

      if (
        !previewContainerWrapper.contains(clickedElement) ||
        !EditDocumentSectionDirective.currentlyActiveSection
      ) {
        return;
      }

      this.shiveRenderService.removeClass(
        EditDocumentSectionDirective.currentlyActiveSection,
        editModeCSSClass,
      );
      this.editDocumentAreaService.clear();
    });

    // Replace placeholder by real content.
    if (this.contentArea().type === SectionType.HTML) {
      this.editDocumentAreaService.fillDynamicFields(this.meta(), this.el);
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  @HostListener('mouseenter')
  public onMouseEnter() {
    if (this.controlsRef || this.mode() === 'print') {
      return;
    }

    this.editDocumentAreaService.hover(this.sectionId());
    this.createEditSectionControl(this.wrapperElement);
    this.shiveRenderService.addClass(this.el.nativeElement, `${editSectionHoveredCSSClass}`);
  }

  private createEditSectionControl(host: HTMLElement): void {
    this.controlsRef = this.vc.createComponent(EditDocumentSectionControlComponent);
    this.controlsRef.setInput('sectionType', this.contentArea().type);
    this.controlsRef.setInput('sectionId', this.sectionId());
    this.controlsRef.setInput('sectionHidden', this.isHidden());
    this.controlsRef.setInput('sectionRemoved', this.isRemoved());
    this.controlsRef.setInput('whiteIcons', true);
    const ctrlEl = this.controlsRef.location.nativeElement;
    this.shiveRenderService.insertBefore(host, ctrlEl, this.el.nativeElement);
  }

  private wrapContent(): void {
    this.wrapperElement = this.shiveRenderService.createElement('div') as HTMLElement;

    // Append CSS classes from config.
    const classes = this.editWrapperConfig()?.classes;
    classes?.forEach((clazz) => this.shiveRenderService.addClass(this.wrapperElement, clazz));

    // Append CSS inline style attributes from config.
    const styles = this.editWrapperConfig()?.styles;
    Object.entries(styles || {}).forEach(([cssProp, cssVal]) =>
      this.shiveRenderService.setStyle(this.el.nativeElement, cssProp, cssVal),
    );

    this.shiveRenderService.addClass(this.wrapperElement, `${editSectionWrapperCSSClass}`);

    const parent = this.el.nativeElement.parentNode;
    this.shiveRenderService.insertBefore(parent, this.wrapperElement, this.el.nativeElement);

    // Move the element inside the new wrapper.
    this.shiveRenderService.appendChild(this.wrapperElement, this.el.nativeElement);
  }

  private destroyEditSectionControl(): void {
    this.controlsRef.destroy();
    this.controlsRef = null;
  }

  private toggleCSSClasses(host: HTMLElement): void {
    if (EditDocumentSectionDirective.currentlyActiveSection) {
      this.shiveRenderService.removeClass(
        EditDocumentSectionDirective.currentlyActiveSection,
        editModeCSSClass,
      );
    }

    this.shiveRenderService.addClass(host, editModeCSSClass);
    EditDocumentSectionDirective.currentlyActiveSection = host;
  }

  private applyIsHiddenOrRemovedStyling(): void {
    runInInjectionContext(this.injector, () => {
      effect(() => {
        const meta = this.contentArea().meta; // Tracking content area change.
        const isRemoved = this.editDocumentAreaService.isRemoved(meta);
        this.isRemoved.set(isRemoved);

        if (isRemoved) {
          this.shiveRenderService.addClass(this.wrapperElement, removedHTMLSectionCSSClass);
        } else {
          this.shiveRenderService.removeClass(this.wrapperElement, removedHTMLSectionCSSClass);
        }

        const isHidden = this.editDocumentAreaService.isHidden(meta);
        // Toggle the CSS class on the wrapper.
        if (isHidden) {
          this.shiveRenderService.addClass(this.wrapperElement, invisibleHTMLSectionCSSClass);
        } else {
          this.shiveRenderService.removeClass(this.wrapperElement, invisibleHTMLSectionCSSClass);
        }

        // Keep the floating controls in sync if they exist.
        this.isHidden.set(isHidden);
      });
    });
  }

  private initSelection(): void {
    this.selected$
      .pipe(
        skip(1),
        filter((section) => section.sectionId === this.sectionId()),
      )
      .subscribe(() => {
        this.toggleCSSClasses(this.el.nativeElement);
      });
  }

  private initHover(): void {
    this.hovered$.pipe(skip(1)).subscribe((hoveredId) => {
      const isMe = hoveredId === this.sectionId();
      if (isMe) {
        this.shiveRenderService.addClass(this.el.nativeElement, editSectionHoveredCSSClass);
      } else {
        this.shiveRenderService.removeClass(this.el.nativeElement, editSectionHoveredCSSClass);
      }
    });
  }
}
